import { Form, Formik } from "formik";
import React, { useContext } from "react";
import { useMessageSource } from "react-message-source";
import { ApplicationFlows } from "../../route/application/authenticated/application-flow/types";
import ApplicationPageContent from "../../ui/layout/page/content/application";
import ContentFooter from "../../ui/layout/page/content/footer";
import NavigationBar from "../../ui/layout/page/content/footer/navigation-bar";
import ContentHeader from "../../ui/layout/page/content/header";
import FormContainer from "../../ui/layout/page/forms-container/form-base-container";
import InputContainer from "../../ui/layout/page/forms-container/form-item-container/input-container";
import RadioButtonContainer from "../../ui/layout/page/forms-container/form-item-container/radio-button-container/input-container";
import RadioButtonField from "../shared/components/formik/radiobutton";
import AmountField from "../../ui/widgets/amount";
import FlowHeader from "../../ui/widgets/flow-header";
import Info from "../../ui/widgets/info";
import {
  mapLanguageToEvent,
  mapYesNoDecisionToEvent,
} from "../../util/tracking/eventActionMapper";
import GTM from "../../util/tracking/gtm";
import { EventCategory } from "../../util/tracking/types";
import Language from "../shared/components/language";
import ProductImage from "../shared/components/product-image";
import { GeneralCompanyInformationModel, YesNo } from "./types";
import useGCIValidationSchema from "./validator";
import { LanguageContext } from "../../context/languageContext";
import { FlexibleInvoicingOption } from "./options/flexible-invoicing";
import { ProductContext } from "../../context/product-context/productContext";
import InvalidFormError, {
  isSubmitErrorMessageShown,
} from "../../ui/layout/page/content/footer/form-error/invalid-form-error";
import { ProductRefType } from "../../context/product-context/types";
import { CancelApplicationContext } from "../../context/cancelApplicationContext";
import InfoTooltip from "../../ui/widgets/tooltip";
import CustomTypography from "../../ui/widgets/typography";
import { IbanAutocomplete } from "../../ui/widgets/iban";

export interface GeneralCompanyInformationProps {
  flowType: ApplicationFlows;
  context: {
    selectedProduct: ProductRefType;
    companyName: string;
  };
  initial?: GeneralCompanyInformationModel;
  next: (data: GeneralCompanyInformationModel) => Promise<void>;
  previous: () => void;
}

export default function GeneralCompanyInformation({
  flowType,
  context,
  initial,
  next,
  previous,
}: GeneralCompanyInformationProps): React.JSX.Element {
  const { getMessageWithNamedParams, getMessage } = useMessageSource();
  const { selectedProduct } = useContext(ProductContext);
  const { language } = useContext(LanguageContext);
  const { setCancelDialogActive } = useContext(CancelApplicationContext);

  const hasFlexibleInvoicingFeature =
    selectedProduct?.hasFeature("FLEXIBLE_INVOICING");

  return (
    <>
      <FlowHeader
        title={getMessage("FlowHeader.basicAccountApplication")}
        image={<ProductImage product={selectedProduct?.productRef} />}
        subTitle={context.companyName}
      />
      <ApplicationPageContent>
        <ContentHeader title={getMessage("Company-details")} />
        <Formik
          key={language}
          initialValues={initial}
          validationSchema={useGCIValidationSchema()}
          onSubmit={async (values, formikActions) => {
            await next(values);
            formikActions.setSubmitting(false);
          }}
        >
          {({
            values,
            handleSubmit,
            setFieldValue,
            isSubmitting,
            submitCount,
            errors,
          }) => {
            return (
              <Form>
                <FormContainer hasAdditionalTopMargin>
                  <InputContainer>
                    <AmountField
                      name="limit"
                      value={values.limit}
                      label={getMessageWithNamedParams(
                        "MasterAccount.requestedLimit",
                        {
                          currency: selectedProduct?.currency,
                        },
                      )}
                      helperUnderInput={
                        <div className="scd-info-tooltip-container">
                          {getMessage(
                            "MasterAccount.requestedLimit-helperUnderInput",
                          )}
                          <InfoTooltip
                            smallIcon
                            title={
                              <CustomTypography
                                variant="body2"
                                dangerouslySetInnerHTML={{
                                  __html: getMessage(
                                    "MasterAccount.requestedLimit-helperUnderInput-description",
                                  ),
                                }}
                              />
                            }
                          >
                            <i className="icon-scd-info" />
                          </InfoTooltip>
                        </div>
                      }
                      onBlur={(e) => {
                        const updatedValue = e.target.value;
                        GTM.trackEvent(
                          EventCategory.BasicAccountLimit,
                          updatedValue &&
                            parseInt(updatedValue.replace(" ", ""), 10),
                        );
                      }}
                    />
                  </InputContainer>
                  <InputContainer>
                    <AmountField
                      value={values.expectedSpendingPerMonth}
                      name="expectedSpendingPerMonth"
                      label={getMessage(
                        "MasterAccount.expectedSpendingPerMonth",
                      )}
                      helperUnderInput={
                        <div className="scd-info-tooltip-container">
                          {getMessage(
                            "MasterAccount.expectedSpendingPerMonth-helperUnderInput",
                          )}
                          <InfoTooltip
                            smallIcon
                            title={
                              <CustomTypography
                                variant="body2"
                                dangerouslySetInnerHTML={{
                                  __html: getMessage(
                                    "MasterAccount.expectedSpendingPerMonth-helperUnderInput-description",
                                  ),
                                }}
                              />
                            }
                          >
                            <i className="icon-scd-info" />
                          </InfoTooltip>
                        </div>
                      }
                    />
                  </InputContainer>
                  <RadioButtonContainer>
                    <RadioButtonField
                      wrap={false}
                      title={getMessage("Card.embossedLine-company")}
                      id="hasCompanyOnCard"
                      name="hasCompanyOnCard"
                      values={[
                        {
                          id: "no",
                          value: YesNo.NO,
                          label: getMessage("YesNo.NO"),
                        },
                        {
                          id: "yes",
                          value: YesNo.YES,
                          label: getMessage("YesNo.YES"),
                        },
                      ]}
                      onChangeCallback={(_, newValue) => {
                        GTM.trackEvent(
                          EventCategory.CompanyNameOnCard,
                          mapYesNoDecisionToEvent(newValue),
                        );
                      }}
                    />
                  </RadioButtonContainer>
                  <RadioButtonContainer>
                    <Language
                      dataPath=""
                      onChange={(_, newValue) =>
                        GTM.trackEvent(
                          EventCategory.CorrespondenceLanguage,
                          `SK - ${mapLanguageToEvent(newValue)}`,
                        )
                      }
                    />
                  </RadioButtonContainer>
                  <IbanAutocomplete
                    iban={values.iban}
                    label={getMessage("BankAccount.iban")}
                    callback={(code: string, name: string) => {
                      setFieldValue("bankCode", code);
                      setFieldValue("bankName", name);
                    }}
                  />
                </FormContainer>
                <FormContainer>
                  {hasFlexibleInvoicingFeature ? (
                    <FlexibleInvoicingOption
                      flexibleInvoicing={values.flexibleInvoicing}
                      hasDecentralizedInvoicing={
                        values.hasDecentralizedInvoicing
                      }
                    />
                  ) : (
                    <Info title={getMessage("Billing.info-label")}>
                      {getMessage("Billing.info")}
                    </Info>
                  )}
                </FormContainer>
                <ContentFooter>
                  <InvalidFormError
                    isDisplayed={isSubmitErrorMessageShown(submitCount, errors)}
                  />
                  {flowType === ApplicationFlows.ADDITIONAL_BAA ? (
                    <NavigationBar
                      onCancel={() => setCancelDialogActive(true)}
                      cancelButtonText={getMessage("Onboarding.cancel")}
                      onNext={() => handleSubmit()}
                      nextButtonText={getMessage("Onboarding.continue")}
                    />
                  ) : (
                    <NavigationBar
                      onCancel={() => setCancelDialogActive(true)}
                      cancelButtonText={getMessage("Onboarding.cancel")}
                      isSubmitting={isSubmitting}
                      onBack={() => previous()}
                      onNext={() => handleSubmit()}
                      backButtonText={getMessage("Onboarding.back")}
                      nextButtonText={getMessage("Onboarding.continue")}
                    />
                  )}
                </ContentFooter>
              </Form>
            );
          }}
        </Formik>
      </ApplicationPageContent>
    </>
  );
}
